/**
 * StepperProgressBar
 *
 * Standalone progress bar component for the stepper that can be used in sheet titles.
 * Updates dynamically based on the current step.
 */

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { ANIMATION_CONSTANTS } from '@/presentation/constants/animationConstants';
import { qaAttr } from '@/presentation/utils/qaAttr';

export interface StepperProgressBarProps {
  /** Total number of steps */
  stepCount: number;

  /** Current active step (0-indexed) */
  currentStep: number;

  /** Direction of navigation (forward/backward) for animation */
  direction?: 'forward' | 'backward';

  /** Optional inactive color (defaults to theme) */
  progressInactiveColor?: string;

  /** Optional active color (defaults to theme) */
  progressActiveColor?: string;

  /** Optional completed color (defaults to theme) */
  progressCompletedColor?: string;

  /** Optional click handler for step segments */
  onStepClick?: (index: number) => void;
}

export const StepperProgressBar: React.FC<StepperProgressBarProps> = ({
  stepCount,
  currentStep,
  direction = 'forward',
  progressInactiveColor,
  progressActiveColor,
  progressCompletedColor,
  onStepClick,
}) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const inactiveColor = progressInactiveColor ?? theme.stepper.progressInactive;
  const activeColor = progressActiveColor ?? theme.stepper.progressActive;
  const completedColor = progressCompletedColor ?? theme.stepper.progressCompleted;

  const steps = Array.from({ length: stepCount }, (_, i) => i);

  return (
    <div
      {...qaAttr({ id: 'stepper-progress', value: `${currentStep + 1}/${stepCount}` })}
      style={{
        display: 'flex',
        gap: `${responsive.stepper.progressGap}px`,
        padding: `${responsive.spacing.sm}px ${responsive.spacing['2xl']}px`,
        width: '100%',
      }}
    >
      {steps.map((index) => {
        const isActive = index === currentStep;
        const isCompleted = index < currentStep;

        // Container style (background layer)
        const segmentContainerStyle: React.CSSProperties = {
          position: 'relative',
          flex: 1,
          height: `${responsive.stepper.progressHeight}px`,
          borderRadius: `${responsive.stepper.progressBorderRadius}px`,
          backgroundColor: inactiveColor,
          overflow: 'hidden',
          cursor: onStepClick ? 'pointer' : 'default',
        };

        // Determine fill width
        let fillWidth = '0%';
        let fillColor = inactiveColor;

        if (isCompleted) {
          fillWidth = '100%';
          fillColor = completedColor;
        } else if (isActive) {
          fillWidth = '100%';
          fillColor = activeColor;
        }

        // Fill layer style (animates horizontally)
        const fillStyle: React.CSSProperties = {
          position: 'absolute',
          top: 0,
          left: direction === 'forward' ? 0 : 'auto',
          right: direction === 'backward' ? 0 : 'auto',
          height: '100%',
          width: fillWidth,
          backgroundColor: fillColor,
          transition: `width ${ANIMATION_CONSTANTS.duration.long}ms ${ANIMATION_CONSTANTS.easing.easeOut}, background-color ${ANIMATION_CONSTANTS.duration.long}ms ${ANIMATION_CONSTANTS.easing.easeOut}`,
          transformOrigin: direction === 'forward' ? 'left' : 'right',
        };

        return (
          <div
            key={index}
            style={segmentContainerStyle}
            onClick={onStepClick ? () => onStepClick(index) : undefined}
            onMouseEnter={onStepClick ? (e) => {
              e.currentTarget.style.transform = 'scaleY(1.5)';
            } : undefined}
            onMouseLeave={onStepClick ? (e) => {
              e.currentTarget.style.transform = 'scaleY(1)';
            } : undefined}
          >
            <div style={fillStyle} />
          </div>
        );
      })}
    </div>
  );
};
